import { useEffect, useState } from "react";
import {
  getEmergenciaTicket, getEmergenciaTickets,
  postEmergenciaTicket, putEmergenciaAtribuir,
} from "../../api/emergencia.api";
import { getSetupResponsaveis, putTicketResponsavel } from "../../api/setup.api";

const SIZE_OPTS  = ["P", "M", "G", "XG"];
const SIZE_LABEL = { P: "Pequeno · 1h", M: "Médio · 9h", G: "Grande · 18h", XG: "Extra Grande · 36h" };
const DEVS_FIXOS = ["Rudi Monteiro", "Marcelo Rossi", "Paulo Lima", "Guilherme Martin"];
const SEM_DEV    = "Sem desenvolvedor";

/* ── Avatar helpers (mesmo mapa do DevKanban.js) ────────────────────── */
const KANBAN_AVATAR_MAP = [
  { match: "paulo",     bg: "#0C447C", color: "#B5D4F4" },
  { match: "guilherme", bg: "#712B13", color: "#F5C4B3" },
  { match: "rudi",      bg: "#3C3489", color: "#CECBF6" },
  { match: "marcelo",   bg: "#085041", color: "#9FE1CB" },
];
function kanbanAvatarCores(nome) {
  if (!nome) return { bg: "#2C2C2A", color: "#B4B2A9" };
  const low = nome.toLowerCase();
  for (const m of KANBAN_AVATAR_MAP) if (low.includes(m.match)) return m;
  return { bg: "#2C2C2A", color: "#B4B2A9" };
}
function kanbanAvatarInitial(nome) {
  if (!nome) return "?";
  const parts = nome.trim().split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[1][0]).toUpperCase()
    : parts[0][0].toUpperCase();
}

/* ══════════════════════════════════════════════════
   Page
══════════════════════════════════════════════════ */
export default function Emergencia() {
  /* ── Busca / adicionar ── */
  const [ticketIdInput, setTicketIdInput] = useState("");
  const [buscando,      setBuscando]      = useState(false);
  const [ticket,        setTicket]        = useState(null);
  const [erroBusca,     setErroBusca]     = useState(null);
  const [size,          setSize]          = useState("");
  const [estimativa,    setEstimativa]    = useState("");
  const [adicionando,   setAdicionando]   = useState(false);

  /* ── Kanban ── */
  const [tickets,      setTickets]      = useState([]);
  const [loadingKanban,setLoadingKanban] = useState(true);
  const [dragging,     setDragging]     = useState(null);
  const [dragOverCol,  setDragOverCol]  = useState(null);
  const [modal,        setModal]        = useState(null);
  const [toast,        setToast]        = useState(null);
  const [filtroStatus, setFiltroStatus] = useState(null); // null | true (fechados) | false (em aberto)
  const [responsaveis, setResponsaveis] = useState([]);

  async function loadKanban() {
    setLoadingKanban(true);
    try {
      const data = await getEmergenciaTickets();
      setTickets(Array.isArray(data) ? data : []);
    } catch {
      setTickets([]);
    } finally {
      setLoadingKanban(false);
    }
  }

  useEffect(() => { loadKanban(); }, []);
  useEffect(() => { getSetupResponsaveis().then(setResponsaveis).catch(() => {}); }, []);

  function showToast(ok, msg) {
    setToast({ ok, msg });
    setTimeout(() => setToast(null), 2500);
  }

  async function handleBuscar() {
    const id = parseInt(ticketIdInput, 10);
    if (!id || id <= 0) return;
    setBuscando(true);
    setErroBusca(null);
    setTicket(null);
    try {
      const data = await getEmergenciaTicket(id);
      setTicket(data);
      setSize(data.size || "");
      setEstimativa(data.e_estimado_analise != null ? String(data.e_estimado_analise) : "");
    } catch (e) {
      setErroBusca(e?.response?.data?.error || "Ticket não encontrado.");
    } finally {
      setBuscando(false);
    }
  }

  async function handleAdicionar() {
    if (!ticket) return;
    setAdicionando(true);
    try {
      const body = { ticket_id: ticket.id };
      if (size) body.size = size;
      if (estimativa !== "") body.e_estimado_analise = Number(estimativa);
      await postEmergenciaTicket(body);
      showToast(true, `#${ticket.id} adicionado à sprint como emergência`);
      setTicket(null);
      setTicketIdInput("");
      setSize("");
      setEstimativa("");
      await loadKanban();
    } catch (e) {
      showToast(false, e?.response?.data?.error || `Erro ao adicionar #${ticket.id}`);
    } finally {
      setAdicionando(false);
    }
  }

  async function handleAtribuir(ticketId, devNome, sizeVal, estimativaVal, responsavelVal, responsavelAnterior) {
    const body = { desenvolvedor: devNome };
    if (sizeVal) body.size = sizeVal;
    if (estimativaVal !== "" && estimativaVal != null) body.e_estimado_analise = Number(estimativaVal);
    await putEmergenciaAtribuir(ticketId, body);
    if (responsavelVal !== (responsavelAnterior || "")) {
      await putTicketResponsavel(ticketId, responsavelVal);
    }
    setModal(null);
    showToast(true, `#${ticketId} atribuído a ${devNome}`);
    await loadKanban();
  }

  function toggleFiltroStatus(valor) {
    setFiltroStatus(prev => (prev === valor ? null : valor));
  }

  function handleDrop(colLabel) {
    setDragOverCol(null);
    if (!dragging || colLabel === SEM_DEV || dragging.desenvolvedor === colLabel) {
      setDragging(null);
      return;
    }
    setModal({ ticket: dragging, devNome: colLabel });
    setDragging(null);
  }

  /* ── Totais para os cards de filtro ── */
  const totalTickets = tickets.length;
  const qtdFechados  = tickets.filter(t => t.fechado).length;
  const qtdAbertos   = totalTickets - qtdFechados;

  /* ── Agrupamento do Kanban ── */
  const ticketsView = filtroStatus === null ? tickets : tickets.filter(t => t.fechado === filtroStatus);
  const semDev = ticketsView.filter(t => !t.desenvolvedor);
  const porDev = new Map();
  for (const t of ticketsView) {
    if (!t.desenvolvedor) continue;
    if (!porDev.has(t.desenvolvedor)) porDev.set(t.desenvolvedor, []);
    porDev.get(t.desenvolvedor).push(t);
  }
  const colunas = [{ nome: SEM_DEV, lista: semDev, isDev: false }];
  for (const nome of DEVS_FIXOS) {
    colunas.push({ nome, lista: porDev.get(nome) ?? [], isDev: true });
  }
  for (const [nome, lista] of porDev.entries()) {
    if (!DEVS_FIXOS.includes(nome)) colunas.push({ nome, lista, isDev: true });
  }

  const jaEhEmergenciaNaSprint = ticket && ticket.emergencia === "1" && ticket.na_sprint_atual;

  return (
    <div style={page}>

      {/* ── Header ── */}
      <header style={header}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <h1 style={h1}>Emergências da Sprint</h1>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            onClick={() => setFiltroStatus(null)}
            style={{ ...kpiPill, ...kpiPillClicavel, borderColor: "rgba(255,255,255,0.12)", ...(filtroStatus === null ? kpiPillAtivo("rgba(255,255,255,0.35)") : null) }}
          >
            <span style={{ color: "#e2e8f0", fontWeight: 800, fontSize: 16 }}>{totalTickets}</span>
            <span style={{ color: "#475569", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>total</span>
          </div>
          <div
            onClick={() => toggleFiltroStatus(false)}
            style={{ ...kpiPill, ...kpiPillClicavel, borderColor: "rgba(251,146,60,0.35)", ...(filtroStatus === false ? kpiPillAtivo("#fb923c") : null) }}
          >
            <span style={{ color: "#fb923c", fontWeight: 800, fontSize: 16 }}>{qtdAbertos}</span>
            <span style={{ color: "#475569", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>em aberto</span>
          </div>
          <div
            onClick={() => toggleFiltroStatus(true)}
            style={{ ...kpiPill, ...kpiPillClicavel, borderColor: "rgba(74,222,128,0.35)", ...(filtroStatus === true ? kpiPillAtivo("#4ade80") : null) }}
          >
            <span style={{ color: "#4ade80", fontWeight: 800, fontSize: 16 }}>{qtdFechados}</span>
            <span style={{ color: "#475569", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>fechados</span>
          </div>
        </div>
      </header>

      <div style={mainRow}>

        {/* ── Coluna esquerda: buscar/adicionar ── */}
        <div style={leftCol}>
          <div style={glassCard}>
            <div style={sectionTitle}>Adicionar Emergência</div>

            <label style={fieldLabel}>Número do ticket</label>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                type="number"
                min="1"
                value={ticketIdInput}
                onChange={e => setTicketIdInput(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") handleBuscar(); }}
                placeholder="Ex: 219271"
                style={{ ...inputStyle, flex: 1 }}
              />
              <button
                onClick={handleBuscar}
                disabled={buscando || !ticketIdInput}
                style={{ ...btnOutline, opacity: (buscando || !ticketIdInput) ? 0.6 : 1, cursor: (buscando || !ticketIdInput) ? "not-allowed" : "pointer" }}
              >
                {buscando ? "Buscando..." : "Buscar"}
              </button>
            </div>

            {erroBusca && (
              <div style={{ ...alertBox, borderColor: "rgba(248,113,113,0.3)", background: "rgba(248,113,113,0.07)", color: "#f87171" }}>
                {erroBusca}
              </div>
            )}

            {ticket && (
              <>
                {/* ── Card do ticket ── */}
                <div style={ticketCardStyle}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                    <span style={{ fontFamily: "monospace", color: "#93c5fd", fontSize: 12, fontWeight: 500 }}>#{ticket.id}</span>
                    {ticket.status && (
                      <span style={badgeNeutral}>{ticket.status}</span>
                    )}
                    {ticket.na_sprint_atual && (
                      <span style={badgeInfo}>Já está na sprint atual</span>
                    )}
                    {ticket.emergencia === "1" && (
                      <span style={badgeWarn}>Já é emergência</span>
                    )}
                    {ticket.programado === "1" && (
                      <span style={badgeProg}>Programado → vira NÃO</span>
                    )}
                  </div>
                  {ticket.subject && (
                    <p style={{ color: "#E5E7EB", fontSize: 13, margin: "4px 0 6px", lineHeight: 1.4 }}>{ticket.subject}</p>
                  )}
                  {ticket.projeto && (
                    <span style={{ color: "rgba(255,255,255,0.3)", fontSize: 10, display: "flex", alignItems: "center", gap: 4 }}>
                      📁 {ticket.projeto}
                    </span>
                  )}
                  {ticket.desenvolvedor && (
                    <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 6 }}>
                      <span style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: 0.5 }}>Atribuído atualmente:</span>
                      <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600 }}>{ticket.desenvolvedor}</span>
                    </div>
                  )}
                </div>

                {/* ── Size ── */}
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <label style={fieldLabel}>Size</label>
                  <div style={{ display: "flex", gap: 6 }}>
                    {SIZE_OPTS.map(opt => {
                      const active = size === opt;
                      return (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => setSize(active ? "" : opt)}
                          title={SIZE_LABEL[opt]}
                          style={{
                            flex: 1, padding: "8px 0", borderRadius: 8,
                            border: active ? "1px solid rgba(96,165,250,0.55)" : "1px solid rgba(96,165,250,0.15)",
                            background: active ? "rgba(96,165,250,0.18)" : "rgba(11,22,42,0.5)",
                            color: active ? "#93c5fd" : "rgba(255,255,255,0.3)",
                            fontSize: 13, fontWeight: active ? 800 : 500,
                            cursor: "pointer", transition: "all 0.15s", letterSpacing: 0.5,
                          }}
                        >{opt}</button>
                      );
                    })}
                  </div>
                  {size && <span style={{ fontSize: 10, color: "rgba(255,255,255,0.3)" }}>{SIZE_LABEL[size]}</span>}
                </div>

                {/* ── Estimativa ── */}
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <label style={fieldLabel}>Estimativa de desenvolvimento (horas)</label>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      placeholder="Ex: 2.5"
                      value={estimativa}
                      onChange={e => setEstimativa(e.target.value)}
                      style={{ ...inputStyle, flex: 1 }}
                    />
                    <span style={{ fontSize: 12, color: "rgba(255,255,255,0.3)" }}>h</span>
                  </div>
                  <span style={{ fontSize: 10, color: "rgba(255,255,255,0.2)" }}>Campo E_Estimado_Analise.</span>
                </div>

                <button
                  onClick={handleAdicionar}
                  disabled={adicionando}
                  style={{ ...btnPrimary, opacity: adicionando ? 0.7 : 1, cursor: adicionando ? "not-allowed" : "pointer" }}
                >
                  {adicionando ? "Salvando..." : jaEhEmergenciaNaSprint ? "Atualizar Emergência" : "Adicionar à Sprint"}
                </button>
              </>
            )}
          </div>
        </div>

        {/* ── Coluna direita: Kanban ── */}
        <div style={{ ...glassCard, flex: 1, minHeight: 0 }}>
          <div style={sectionTitle}>
            Kanban de Emergências na Sprint Atual
            {filtroStatus !== null && (
              <span style={{ color: filtroStatus ? "#4ade80" : "#fb923c", textTransform: "none", letterSpacing: 0 }}>
                {" "}· {filtroStatus ? "fechados" : "em aberto"}
              </span>
            )}
          </div>

          {loadingKanban ? (
            <div style={{ color: "#475569", fontSize: 13 }}>Carregando...</div>
          ) : (
            <div style={board}>
              {colunas.map(col => (
                <EmergColumn
                  key={col.nome}
                  label={col.nome}
                  tickets={col.lista}
                  isDev={col.isDev}
                  isOver={dragOverCol === col.nome}
                  onDragOver={e => { e.preventDefault(); setDragOverCol(col.nome); }}
                  onDragLeave={() => setDragOverCol(null)}
                  onDrop={() => handleDrop(col.nome)}
                  onCardDragStart={t => setDragging(t)}
                  onCardDragEnd={() => setDragging(null)}
                />
              ))}
            </div>
          )}
        </div>

      </div>

      {/* ── Modal de atribuição ── */}
      {modal && (
        <AtribuirEmergModal
          ticket={modal.ticket}
          devNome={modal.devNome}
          responsaveis={responsaveis}
          onClose={() => setModal(null)}
          onConfirm={(sizeVal, estVal, respVal) => handleAtribuir(modal.ticket.id, modal.devNome, sizeVal, estVal, respVal, modal.ticket.responsavel)}
        />
      )}

      {toast && (
        <div style={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          padding: "10px 20px", borderRadius: 10, zIndex: 9999,
          background: toast.ok ? "rgba(74,222,128,0.15)" : "rgba(248,113,113,0.15)",
          border: `1px solid ${toast.ok ? "rgba(74,222,128,0.4)" : "rgba(248,113,113,0.4)"}`,
          color: toast.ok ? "#4ade80" : "#f87171",
          fontSize: 13, fontWeight: 700, boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
          backdropFilter: "blur(12px)", pointerEvents: "none",
        }}>
          {toast.ok ? "✓" : "✗"} {toast.msg}
        </div>
      )}
    </div>
  );
}

/* ── EmergColumn ─────────────────────────────────────────────────── */
function EmergColumn({ label, tickets, isDev, isOver, onDragOver, onDragLeave, onDrop, onCardDragStart, onCardDragEnd }) {
  const av = kanbanAvatarCores(isDev ? label : null);

  return (
    <div
      style={{
        ...colStyle,
        border: isOver ? "1px solid rgba(37,99,235,0.6)" : colStyle.border,
        boxShadow: isOver ? "0 0 24px rgba(37,99,235,0.2), 0 8px 32px rgba(0,0,0,0.5)" : colStyle.boxShadow,
      }}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <div style={{ ...colHeader, background: isOver ? "rgba(37,99,235,0.06)" : "rgba(255,255,255,0.02)", transition: "background 0.15s" }}>
        <div style={{ ...devAvatar, background: av.bg, color: av.color }}>
          {isDev ? kanbanAvatarInitial(label) : "—"}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={colTitle}>{label}</div>
          <span style={pill}>{tickets.length} ticket(s)</span>
        </div>
      </div>
      <div style={cardList}>
        {tickets.length === 0 ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8, padding: "24px 0" }}>
            <span style={{ fontSize: 20, color: "rgba(255,255,255,0.1)", lineHeight: 1 }}>□</span>
            <span style={{ fontSize: 11, color: "rgba(255,255,255,0.15)" }}>sem cards</span>
          </div>
        ) : tickets.map(t => (
          <EmergCard
            key={t.id}
            ticket={t}
            onDragStart={() => onCardDragStart(t)}
            onDragEnd={onCardDragEnd}
          />
        ))}
      </div>
    </div>
  );
}

/* ── EmergCard ───────────────────────────────────────────────────── */
function EmergCard({ ticket, onDragStart, onDragEnd }) {
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDoubleClick={() => window.open(`https://redmine.lexsis.com.br/issues/${ticket.id}`, "_blank")}
      title="Arraste para atribuir a um desenvolvedor · Duplo clique para abrir no Redmine"
      style={emergCardStyle}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontFamily: "monospace", fontSize: 11, fontWeight: 500, color: "#fb923c" }}>#{ticket.id}</span>
        <div style={{ display: "flex", gap: 4 }}>
          {ticket.size && <span style={badgeSize}>{ticket.size}</span>}
          {ticket.e_estimado_analise != null && <span style={badgeEst}>{ticket.e_estimado_analise}h</span>}
        </div>
      </div>
      {ticket.subject
        ? <p style={{ fontSize: 12, color: "#D1D5DB", lineHeight: 1.45, margin: 0, marginTop: 6 }}>{ticket.subject}</p>
        : <p style={{ fontSize: 12, color: "rgba(255,255,255,0.2)", fontStyle: "italic", margin: 0, marginTop: 6 }}>Ver no Redmine →</p>
      }
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4 }}>
        {ticket.projeto && <span style={{ fontSize: 9, color: "rgba(255,255,255,0.25)" }}>📁 {ticket.projeto}</span>}
        {ticket.status && <span style={{ fontSize: 9, color: "rgba(255,255,255,0.25)" }}>{ticket.status}</span>}
      </div>
    </div>
  );
}

/* ── AtribuirEmergModal ──────────────────────────────────────────── */
function AtribuirEmergModal({ ticket, devNome, responsaveis, onClose, onConfirm }) {
  const [saving,      setSaving]      = useState(false);
  const [erro,        setErro]        = useState(null);
  const [size,        setSize]        = useState(ticket.size || "");
  const [estimativa,  setEstimativa]  = useState(ticket.e_estimado_analise != null ? String(ticket.e_estimado_analise) : "");
  const [responsavel, setResponsavel] = useState(ticket.responsavel || "");

  async function handleConfirm() {
    setSaving(true); setErro(null);
    try {
      await onConfirm(size, estimativa, responsavel);
    } catch {
      setErro("Erro ao atribuir ticket. Tente novamente.");
      setSaving(false);
    }
  }

  const av = kanbanAvatarCores(devNome);

  return (
    <div style={overlay} onClick={saving ? undefined : onClose}>
      <div style={modalBox} onClick={e => e.stopPropagation()}>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ color: "#E5E7EB", fontSize: 15, fontWeight: 500, margin: 0 }}>Atribuir Emergência</h2>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              width: 28, height: 28, borderRadius: 6,
              background: "rgba(255,255,255,0.06)",
              border: "0.5px solid rgba(255,255,255,0.1)",
              color: "rgba(255,255,255,0.4)",
              fontSize: 12, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >✕</button>
        </div>

        <div style={ticketCardStyle}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span style={{ fontFamily: "monospace", color: "#93c5fd", fontSize: 12, fontWeight: 500 }}>#{ticket.id}</span>
            {ticket.status && <span style={badgeNeutral}>{ticket.status}</span>}
          </div>
          {ticket.subject && (
            <p style={{ color: "#E5E7EB", fontSize: 13, margin: "4px 0 6px", lineHeight: 1.4 }}>{ticket.subject}</p>
          )}
          {ticket.desenvolvedor && (
            <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 6 }}>
              <span style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: 0.5 }}>Atribuído atualmente:</span>
              <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600 }}>{ticket.desenvolvedor}</span>
            </div>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{
            width: 28, height: 28, borderRadius: "50%",
            background: "rgba(37,99,235,0.2)",
            border: "0.5px solid rgba(37,99,235,0.3)",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#93c5fd", fontSize: 14,
          }}>↓</div>
        </div>

        <div style={{
          background: "rgba(255,255,255,0.04)",
          border: "0.5px solid rgba(255,255,255,0.1)",
          borderRadius: 8, padding: "12px 14px",
        }}>
          <div style={{ fontSize: 10, fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.3)", marginBottom: 6 }}>
            Atribuir para
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 500, color: "#E5E7EB" }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: av.bg, color: av.color, fontSize: 11, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              {kanbanAvatarInitial(devNome)}
            </div>
            <span style={{ color: "#60a5fa", fontWeight: 800, fontSize: 15 }}>{devNome}</span>
          </div>
        </div>

        {/* Responsável (custom field Redmine) */}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: 0.7 }}>
            Responsável
          </label>
          <select
            value={responsavel}
            onChange={e => setResponsavel(e.target.value)}
            disabled={saving}
            style={{
              padding: "9px 12px", borderRadius: 8,
              border: "1px solid rgba(96,165,250,0.2)",
              background: "rgba(11,22,42,0.7)",
              color: "#e2e8f0", fontSize: 13, outline: "none",
              fontFamily: "Inter, sans-serif", cursor: saving ? "not-allowed" : "pointer",
            }}
          >
            <option value="">— sem responsável —</option>
            {responsaveis.map(nome => (
              <option key={nome} value={nome}>{nome}</option>
            ))}
          </select>
        </div>

        {/* Size */}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: 0.7 }}>Size</label>
          <div style={{ display: "flex", gap: 6 }}>
            {SIZE_OPTS.map(opt => {
              const active = size === opt;
              return (
                <button
                  key={opt}
                  type="button"
                  disabled={saving}
                  onClick={() => setSize(active ? "" : opt)}
                  title={SIZE_LABEL[opt]}
                  style={{
                    flex: 1, padding: "8px 0", borderRadius: 8,
                    border: active ? "1px solid rgba(96,165,250,0.55)" : "1px solid rgba(96,165,250,0.15)",
                    background: active ? "rgba(96,165,250,0.18)" : "rgba(11,22,42,0.5)",
                    color: active ? "#93c5fd" : "rgba(255,255,255,0.3)",
                    fontSize: 13, fontWeight: active ? 800 : 500,
                    cursor: saving ? "not-allowed" : "pointer",
                    transition: "all 0.15s", letterSpacing: 0.5,
                  }}
                >{opt}</button>
              );
            })}
          </div>
        </div>

        {/* Estimativa */}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: 0.7 }}>
            Estimativa de desenvolvimento (horas)
          </label>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="number"
              min="0"
              step="0.5"
              placeholder="Ex: 2.5"
              value={estimativa}
              onChange={e => setEstimativa(e.target.value)}
              disabled={saving}
              style={{
                flex: 1, padding: "9px 12px", borderRadius: 8,
                border: "1px solid rgba(96,165,250,0.2)",
                background: "rgba(11,22,42,0.7)",
                color: "#e2e8f0", fontSize: 14, outline: "none",
                fontFamily: "Inter, sans-serif",
              }}
            />
            <span style={{ fontSize: 12, color: "rgba(255,255,255,0.3)" }}>h</span>
          </div>
        </div>

        {erro && (
          <div style={{ color: "#f87171", fontSize: 12, padding: "8px 12px", borderRadius: 8, background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.25)" }}>
            ⚠ {erro}
          </div>
        )}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onClose} disabled={saving} style={btnSec}>Cancelar</button>
          <button
            onClick={handleConfirm}
            disabled={saving}
            style={{ ...btnConfirm, opacity: saving ? 0.7 : 1, cursor: saving ? "not-allowed" : "pointer" }}
          >
            {saving ? "Atribuindo..." : "Confirmar atribuição"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Styles ── */
const page = { display: "flex", flexDirection: "column", gap: 16, height: "calc(100vh - 56px)", overflow: "hidden" };
const header = { display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexShrink: 0 };
const h1 = { fontSize: 22, fontWeight: 800, color: "#f1f5f9", letterSpacing: -0.5 };

const pill = {
  padding: "3px 10px", borderRadius: 20,
  background: "rgba(255,255,255,0.06)",
  border: "0.5px solid rgba(255,255,255,0.1)",
  color: "rgba(255,255,255,0.5)",
  fontSize: 11, fontWeight: 700,
};

const kpiPill = {
  display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 1,
  background: "rgba(11,22,42,0.72)", border: "1px solid", borderRadius: 10,
  padding: "6px 14px",
};

const kpiPillClicavel = { cursor: "pointer", userSelect: "none" };

const kpiPillAtivo = (cor) => ({
  borderColor: cor,
  background: "rgba(255,255,255,0.06)",
  boxShadow: `0 0 0 1px ${cor} inset`,
});

const glassCard = {
  background: "rgba(11,22,42,0.72)",
  backdropFilter: "blur(18px)",
  WebkitBackdropFilter: "blur(18px)",
  border: "1px solid rgba(96,165,250,0.1)",
  borderRadius: 14,
  padding: "18px 20px",
  display: "flex",
  flexDirection: "column",
  gap: 12,
  overflow: "hidden",
};

const mainRow = { display: "flex", gap: 14, flex: 1, minHeight: 0, overflow: "hidden" };
const leftCol = { display: "flex", flexDirection: "column", gap: 14, width: 360, flexShrink: 0, minHeight: 0, overflowY: "auto" };

const sectionTitle = { fontSize: 11, fontWeight: 700, color: "#cbd5e1", textTransform: "uppercase", letterSpacing: 1.2, flexShrink: 0 };
const fieldLabel = { fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.6 };

const inputStyle = {
  padding: "8px 12px",
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(96,165,250,0.18)",
  borderRadius: 8,
  color: "#e2e8f0",
  fontSize: 13,
  outline: "none",
  fontFamily: "Inter, sans-serif",
};

const alertBox = { padding: "8px 12px", borderRadius: 8, border: "1px solid", fontSize: 12 };

const btnPrimary = {
  padding: "10px 20px",
  borderRadius: 9,
  border: "1px solid rgba(96,165,250,0.4)",
  background: "rgba(96,165,250,0.15)",
  color: "#60a5fa",
  fontSize: 13,
  fontWeight: 700,
  cursor: "pointer",
  transition: "opacity 0.15s",
};

const btnOutline = {
  padding: "8px 14px",
  borderRadius: 8,
  border: "1px solid rgba(96,165,250,0.2)",
  background: "rgba(96,165,250,0.06)",
  color: "#60a5fa",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
};

const ticketCardStyle = {
  background: "rgba(255,255,255,0.04)",
  border: "0.5px solid rgba(255,255,255,0.08)",
  borderLeft: "3px solid #2563eb",
  borderRadius: "0 8px 8px 0",
  padding: "12px 14px",
};

const badgeNeutral = { fontSize: 10, padding: "2px 7px", borderRadius: 99, border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.45)", background: "rgba(255,255,255,0.04)" };
const badgeInfo    = { fontSize: 10, padding: "2px 7px", borderRadius: 99, border: "1px solid rgba(34,211,238,0.2)", color: "#22d3ee", background: "rgba(34,211,238,0.06)" };
const badgeWarn    = { fontSize: 10, padding: "2px 7px", borderRadius: 99, border: "1px solid rgba(251,146,60,0.25)", color: "#fb923c", background: "rgba(251,146,60,0.08)" };
const badgeProg    = { fontSize: 10, padding: "2px 7px", borderRadius: 99, border: "1px solid rgba(248,113,113,0.25)", color: "#f87171", background: "rgba(248,113,113,0.08)" };
const badgeSize    = { fontSize: 9, fontWeight: 800, padding: "1px 6px", borderRadius: 6, border: "1px solid rgba(96,165,250,0.3)", color: "#93c5fd", background: "rgba(96,165,250,0.1)" };
const badgeEst     = { fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 6, border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.4)", background: "rgba(255,255,255,0.04)" };

/* Kanban */
const board = {
  display: "flex", gap: 12,
  overflowX: "auto", overflowY: "hidden",
  flex: 1, paddingBottom: 8, alignItems: "stretch",
};

const colStyle = {
  background: "rgba(11,22,42,0.72)",
  backdropFilter: "blur(18px)",
  WebkitBackdropFilter: "blur(18px)",
  border: "1px solid rgba(96,165,250,0.12)",
  borderRadius: 14,
  boxShadow: "0 8px 32px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04)",
  flex: "1 1 180px", minWidth: 180,
  display: "flex", flexDirection: "column",
  overflow: "hidden",
};

const colHeader = {
  display: "flex", alignItems: "center", gap: 10,
  padding: "12px 14px",
  borderBottom: "1px solid rgba(96,165,250,0.08)",
};

const devAvatar = {
  width: 36, height: 36, borderRadius: 10,
  display: "flex", alignItems: "center", justifyContent: "center",
  fontWeight: 700, fontSize: 14, flexShrink: 0,
};

const colTitle = { color: "#f1f5f9", fontSize: 13, fontWeight: 800, letterSpacing: -0.2, marginBottom: 4 };

const cardList = { display: "flex", flexDirection: "column", padding: "10px 10px", overflowY: "auto", flex: 1, gap: 8 };

const emergCardStyle = {
  background: "rgba(255,255,255,0.04)",
  border: "0.5px solid rgba(255,255,255,0.08)",
  borderLeft: "3px solid #fb923c",
  borderRadius: "0 8px 8px 0",
  padding: "10px 12px",
  cursor: "grab",
  userSelect: "none",
  transition: "all 0.15s ease",
  display: "flex", flexDirection: "column",
};

/* Modal */
const overlay  = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 };
const modalBox = { background: "#13151C", border: "0.5px solid rgba(255,255,255,0.1)", borderRadius: 12, padding: "20px 24px", width: 420, maxWidth: "92vw", boxShadow: "0 28px 72px rgba(0,0,0,0.8)", display: "flex", flexDirection: "column", gap: 14 };
const btnSec    = { padding: "8px 16px", borderRadius: 8, border: "0.5px solid rgba(255,255,255,0.12)", background: "transparent", color: "rgba(255,255,255,0.45)", fontSize: 13, cursor: "pointer", transition: "all 0.15s" };
const btnConfirm = { padding: "8px 20px", borderRadius: 8, border: "none", background: "#2563eb", color: "#eff6ff", fontSize: 13, fontWeight: 500, cursor: "pointer", transition: "background 0.15s" };
